import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UsersService } from 'src/app/services/users/users.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { RolesAffectUsersComponent } from './roles-affect-users/roles-affect-users.component';
import { RoleDeleteConfirmComponent } from './role-delete-confirm/role-delete-confirm.component';
import { entAnimations } from 'src/app/animations';
import {
  Observable,
  Subscription,
  catchError,
  forkJoin,
  lastValueFrom,
  map,
  mergeMap,
  of,
} from 'rxjs';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { UserInfo } from 'src/app/models/user-info.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { OrgsService } from 'src/app/services/orgs/orgs.service';
import { OrgModel } from 'src/app/models/org.model';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class RolesComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  isLoading = false;
  assignableRoles: OrgModel[];
  selectedRoleOid: string;
  private selectedRole: OrgModel;
  private currentUser: UserInfo;
  private selectedEtablissement: string;
  isLoadingMembers = false;
  private resetSearchFields = false;
  hasnoAssignableRole = false;
  errorGetRoles: Error = { error: { message: '', code: '' } };
  isErrorGetRoles = false;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  dataSource: MatTableDataSource<UserInfo>;

  displayedColumns: string[] = [];
  expandedElement: UserInfo | null;
  windowWidth: number = window.innerWidth;
  sortedValue: string;

  private currentGeSub: Subscription;

  constructor(
    private _orgService: OrgsService,
    private _userService: UsersService,
    private _settingsService: SettingsService,
    private _router: Router,
    private _matDialog: MatDialog,
    private _localStorageService: LocalStorageService,
    private _toolboxService: ToolBoxService
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentUser = this._userService.currentUserValue;
    if (this.currentUser == null) {
      this._router.navigate(['/login']);
    }

    this.currentGeSub = this._settingsService.currentGE$.subscribe(ge => {
      this.selectedEtablissement = ge;
      this.onGroupEtablissementChange();
    });

    this.selectedEtablissement = await this._toolboxService.getUserEtablissement();

    this.getMyAssignableOrgs(this.selectedEtablissement, true)
      .pipe(
        mergeMap(orgs => {
          if (orgs.length > 0) {
            return forkJoin({
              members: this.getOrgMembers(orgs[0].oid),
            });
          }
          return forkJoin({
            members: of(new Array<OrgModel>()),
            users: of(new Array<UserInfo>()),
          });
        })
      )
      .subscribe();

    this.updateDisplayedColumns();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  private updateDisplayedColumns(): void {
    if (this.windowWidth < 400) {
      this.displayedColumns = ['firstName', 'lastName', 'expand'];
    } else if (this.windowWidth < 500) {
      this.displayedColumns = ['firstName', 'lastName', 'delete', 'expand'];
    } else {
      this.displayedColumns = ['firstName', 'lastName', 'username', 'delete'];
    }
  }

  ngOnDestroy(): void {
    this._localStorageService.removeItem('assignableOrgs');
    this._localStorageService.removeItem('contextStorageUsersRole');
    this._localStorageService.removeItem('UsersRole');
    this.currentGeSub.unsubscribe();
  }

  deleteUser(e: UserInfo): void {
    const dialogRef = this._matDialog.open(RoleDeleteConfirmComponent, {
      panelClass: 'app-role-delete-confirm-dialog',
      data: {
        roletodelete: this.selectedRole,
        usertounassign: e.username,
        userOidToUnassign: e.oid,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.onRoleChange();
    });
  }

  affectUsers(): void {
    const dialogRef = this._matDialog.open(RolesAffectUsersComponent, {
      panelClass: 'app-roles-affect-users-dialog',
      data: {
        action: 'new',
        currentEtab: this.selectedEtablissement,
        selectedRole: this.selectedRole,
        resetSearchFields: this.resetSearchFields,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.onRoleChange();
    });
    this.resetSearchFields = false;
  }

  /**
   * Triggered by the role/org selector.
   */
  onRoleChange() {
    this.selectedRole = this.assignableRoles.find(
      x => x.oid == this.selectedRoleOid
    );
    this.getOrgMembers(this.selectedRoleOid).subscribe();
  }

  /**
   * Triggered by the groupe etablissement selector or on component Init.
   * @param fromStorage
   */
  onGroupEtablissementChange(fromStorage = false) {
    this.resetSearchFields = true;
    this._localStorageService.removeItem('myUsers');

    this.getMyAssignableOrgs(this.selectedEtablissement, fromStorage)
      .pipe(
        mergeMap(orgs => {
          if (orgs.length > 0) {
            return forkJoin({
              members: this.getOrgMembers(orgs[0].oid),
            });
          }
          return forkJoin({
            members: of(new Array<OrgModel>()),
            users: of(new Array<UserInfo>()),
          });
        })
      )
      .subscribe();
  }

  /**
   * Returns a list of roles based on the rights of the connected user for a specific establishement.
   * @param establishementName Groupe etablissement name.
   * @param fromStorage allows to fetch values from Midpoint.
   * @returns A list of roles (may be empty).
   */
  private getMyAssignableOrgs(
    establishementName: string,
    fromStorage: boolean
  ): Observable<OrgModel[]> {
    this.hasnoAssignableRole = true;
    this.isErrorGetRoles = false;

    if (fromStorage) {
      //Tries to retrieve org list from cache
      const orgs =
        this._localStorageService.getItem<OrgModel[]>('assignableOrgs');
      //When cache entry exists, deserialize value and set global varibles if possible
      if (orgs !== null && orgs.length > 0) {
        if (establishementName === orgs[0].groupeEtablissement) {
          this.assignableRoles = orgs.sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
          this.selectedRoleOid = this.assignableRoles[0].oid;
          this.selectedRole = this.assignableRoles[0];
          this.hasnoAssignableRole = false;
          return of(orgs);
        }
      }
    }

    //Remove cache entry and request orgs to Midpoint
    this._localStorageService.removeItem('assignableOrgs');
    this.isLoading = true;
    return this._orgService.getMyAssignableRole(establishementName).pipe(
      map(orgs => {
        this.isLoading = false;

        if (orgs !== null && orgs.length > 0) {
          this.assignableRoles = orgs.sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
          this.selectedRoleOid = this.assignableRoles[0].oid;
          this.selectedRole = this.assignableRoles[0];

          this.hasnoAssignableRole = false;
          return orgs;
        }
        return new Array<OrgModel>();
      }),
      catchError(error => {
        this.isLoading = false;
        this.isErrorGetRoles = false;
        throw error;
      })
    );
  }

  /**
   * Returns a list of users with a specific role oid.
   * @param roleOid
   * @returns A list of users (may be empty).
   */
  private getOrgMembers(orgOid: string): Observable<UserInfo[]> {
    this.isLoadingMembers = true;
    return this._orgService.getMembers(orgOid).pipe(
      map(users => {
        this.isLoadingMembers = false;
        this.dataSource = new MatTableDataSource(users);
        this.dataSource.paginator = this.paginator;
        return users;
      }),
      catchError(error => {
        this.isLoadingMembers = false;
        throw error;
      })
    );
  }

  sortData(sort: Sort) {
    this.sortedValue = sort.active;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    if (!sort.active || sort.direction === '') {
      this.sortedValue = null;
      return;
    }

    this.dataSource.data = this.dataSource.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName':
          return this.compare(
            a.firstName.toLocaleLowerCase(),
            b.firstName.toLocaleLowerCase(),
            isAsc
          );
        case 'lastName':
          return this.compare(
            a.lastName.toLocaleLowerCase(),
            b.lastName.toLocaleLowerCase(),
            isAsc
          );
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleRow(element: UserInfo): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
